import type { Meeting, Tag } from "@repo/types";
import type { Row } from "@tanstack/react-table";
import type { Table } from "@tanstack/table-core";
import { TagList } from "./TagList";

export type TagSource =
  | "meeting"
  | "agenda"
  | "agendaModule"
  | "agendaItem"
  | "document";

export type TagWSource = Tag & { source: TagSource };

/** DataTable filter function for 'tags' column */
export function tableTagsFilter<T>(
  row: Row<T>,
  columnId: string,
  filterValue: string
) {
  filterValue = filterValue.toLowerCase();
  const tags = row.getValue<Tag[] | undefined>(columnId);
  return !!tags?.some((tag) => tag.text.toLowerCase().includes(filterValue));
}

/** DataTable cell render function for 'tags' column (activates clickable tag filtering) */
export function tableTagsRender<T>({
  tagsKey = "tags" as keyof T,
}: {
  tagsKey?: keyof T;
} = {}) {
  return ({ row, table }: { row: Row<T>; table: Table<T> }) => (
    <TagList row={row} table={table} tagsKey={tagsKey} />
  );
}

export const uniqueTags = (tags: Tag[]) => {
  const tagsMap = new Map();
  tags.forEach((tag) => tagsMap.set(tag.text, tag));
  return [...tagsMap.values()];
};

export const uniqueTagsWSource = (tags: TagWSource[]) => {
  const tagsMap = new Map<string, TagWSource>();
  tags.forEach((tag) => tagsMap.set(tag.source + ":" + tag.text, tag));
  return [...tagsMap.values()];
};

export const addNestedMeetingTags = (
  meeting: Meeting,
  include: TagSource[]
): Meeting & { $nestedTags: TagWSource[] } => {
  const allTags: (Tag & { source: TagSource })[] = [];

  if (!include.length) return { ...meeting, $nestedTags: [] };

  const pushTags = (tags: Tag[] | undefined, source: TagSource) => {
    tags?.forEach((tag) => allTags.push({ ...tag, source }));
  };

  const incMeeting = include.includes("meeting");
  const incAgenda = include.includes("agenda");
  const incAgendaModule = include.includes("agendaModule");
  const incAgendaItem = include.includes("agendaItem");
  const incDocument = include.includes("document");

  const subAgendaItem = incAgendaItem || incDocument;
  const subAgendaModule = incAgendaModule || subAgendaItem;

  // push meeting tags
  if (incMeeting) pushTags(meeting.tags, "meeting");

  // push agenda tags
  if (incAgenda) pushTags(meeting.agenda?.tags, "agenda");

  if (subAgendaModule) {
    meeting.agenda?.agendaModules?.map((agendaModule) => {
      // push agenda module tags
      if (incAgendaModule) pushTags(agendaModule.tags, "agendaModule");

      if (subAgendaItem) {
        agendaModule.agendaItems?.map((agendaItem) => {
          // push agenda item tags
          if (incAgendaItem) pushTags(agendaItem.tags, "agendaItem");

          if (incDocument) {
            agendaItem.documents?.map((document) => {
              // push document tags
              pushTags(document.tags, "document");
            });
          }
        });
      }
    });
  }

  return {
    ...meeting,
    $nestedTags: uniqueTagsWSource(allTags),
  };
};
